import { Component, ElementRef, forwardRef, Inject, Input } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { firstValueFrom } from "rxjs";
import { ErrorMessages, Permissions } from '../../../common/constants';
import { HistoricalAlertWorkSessionMatPaginatorIntl } from "../../../dashboard-area/shared/historical-alert-work-session-mat-paginator-intl.service";
import { WorkSessionService } from "../../../dashboard-area/shared/work-session.service";
import { ListRangeSelectionModeType, PagedList, WorkSession, WorkSessionDefinition } from "../../../model";
import { AbstractExportContextService } from "../../../service/abstract-export-context.service";
import { AppService } from "../../../service/app.service";
import { AuthenticationService } from "../../../service/authentication.service";
import { FieldService } from "../../../service/field.service";
import { NavigationService } from "../../../service/navigation.service";
import { ThingContextService } from "../../../service/thing-context.service";
import { WorkSessionAdvancedSearchImportDialogComponent } from "../../../shared/advanced-search/work-session-advanced-search/work-session-advanced-search-import-dialog.component";
import { ListSnackbarButtonType } from "../../../shared/component/list-snackbar/list-snackbar.component";
import { ConfirmDialog } from "../../../shared/confirm-dialog/confirm-dialog.component";
import { CustomTableService } from "../../../shared/custom-table";
import { DownloadProgressBarDialogComponent } from "../../../shared/download-progress-bar-dialog/download-progress-bar-dialog";
import { BulkControl, DetailsModeType } from "../../../shared/list-widget-v2/list-widget-v2.components";
import { SearchTargetType } from "../../../shared/search-field/search-field.component";
import { ErrorUtility } from "../../../utility/error-utility";
import { SnackbarService } from "../../shared/snackbar.service";
import { WidgetPrintResizeCoordinatorService } from "../../shared/widget-print-resize-coordinator.service";
import { ThingListWidgetV2Service } from "../../thing-list/thing-list-widget-v2.service";
import { AbstractWorkSessionListWidgetV2Component } from "../abstract-work-session-list-widget-v2.component";
import { HistoricalWorkSessionListWidgetV2Service } from "./historical-work-session-list-widget-v2.service";

@Component({
    selector: 'historical-work-session-list-widget-v2',
    template: require('./historical-work-session-list-widget-v2.component.html'),
    providers: [HistoricalWorkSessionListWidgetV2Service, ThingListWidgetV2Service, WorkSessionService, { provide: MatPaginatorIntl, useClass: HistoricalAlertWorkSessionMatPaginatorIntl }],
    styles: [require('../../../shared/list-widget-v2/list-widget-v2.css')]
})
export class HistoricalWorkSessionListWidgetV2Component extends AbstractWorkSessionListWidgetV2Component {

    @Input() reportFileName: string;

    @Input() addActionEnabled: boolean = true;

    @Input() reconcileConnectionStatus: boolean;

    manualActivationWorkSessionDefinitions: WorkSessionDefinition[] = [];
    enableImport: boolean;
    hidden: boolean;
    bulkControls: BulkControl[] = [];

    private writeWorkSessionPermission: boolean;

    constructor(
        @Inject(forwardRef(() => HistoricalWorkSessionListWidgetV2Service)) private historicalWorkSessionListWidgetV2Service: HistoricalWorkSessionListWidgetV2Service,
        @Inject(forwardRef(() => NavigationService)) protected navigationService: NavigationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => WorkSessionService)) workSessionService: WorkSessionService,
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AbstractExportContextService)) exportService: AbstractExportContextService,
        @Inject(forwardRef(() => ThingContextService)) private thingContextService: ThingContextService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => SnackbarService)) snackbarService: SnackbarService,
        @Inject(forwardRef(() => FieldService)) fieldService: FieldService,
        @Inject(forwardRef(() => ElementRef)) elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) coordinator: WidgetPrintResizeCoordinatorService,
    ) {
        super(historicalWorkSessionListWidgetV2Service, navigationService, appService, workSessionService, authenticationService, exportService, dialog, snackbarService, fieldService, elementRef, coordinator);
    }

    protected init(): void {
        this.searchFieldId = 'historical-work-session-list-query';
        this.popupSearchTarget = SearchTargetType.HISTORICAL_WORK_SESSIONS;
        this.checkIfHidden();
        this.writeWorkSessionPermission = this.authenticationService.hasPermission(Permissions.WRITE_WORK_SESSION);
        this.localStorageKey = 'historicalWorkSessionAdvancedSearchFieldsValues';
        if (this.thingContextService.getCurrentThing() && this.thingContextService.getCurrentThing().thingDefinitionId) {
            this.workSessionService.getThingDefinitionWorkSessionDefinitions(this.thingContextService.getCurrentThing().thingDefinitionId, true).then(ws => {
                this.manualActivationWorkSessionDefinitions = ws.filter(w => w.manualActivation);
                this.enableImport = this.writeWorkSessionPermission && !!this.manualActivationWorkSessionDefinitions?.length;
            });
        }
        this.preserveSelectedBetweenPages = true;
        this.bulkControlsEnabled = this.bulkControlsEnabled && !!this.thingContextService.getCurrentThing();
    }

    private checkIfHidden(): void {
        if (!this.thingContextService.getCurrentThing()) {
            if (this.authenticationService.isOrganizationUser() || this.authenticationService.isPartnerUser()) {
                this.hidden = !this.authenticationService.hasFeature('multipleThingAggregationForOrgPartner');
            } else {
                this.hidden = !this.authenticationService.hasFeature('multipleThingAggregationForCustomer');
            }
        }
    }

    protected updatePaginatorClass(): void {
        this.matPaginatorClass = "mat-paginator-hidden-last ";
        if (this.authenticationService.getTenant().listRangeSelectionMode == ListRangeSelectionModeType.PAGES) {
            this.matPaginatorClass += "mat-paginator-pages-mode";
        } else {
            this.matPaginatorClass += "mat-paginator-hidden-range-label";
        }
    }

    protected getWorkSessionList(): void {
        this.historicalWorkSessionListWidgetV2Service.getPagedList(this.pageIndex, this.pageSize, this.sort, null, this.searchFields, this.advancedSearchBody, null, this.includeSubThingsWorkSessions).then(pagedList => {
            this.getDisplayedColumns();
            if (!this.historicalWorkSessionListWidgetV2Service.isSortEnabled()) {
                this.sort = [];
            }
            this.error = null;
            this.historicalWorkSessionListWidgetV2Service.updatePageListContent(pagedList.content, this.workSessionDefinitions, this.displayedColumns, true, this.detailsMode, this.clickOnRowBehaviour);
            if (this.writeWorkSessionPermission && pagedList.content.some(ws => ws.manualActivation)) {
                if (!this.displayedColumns.some(c => c.name == 'delete')) {
                    this.displayedColumns.push(CustomTableService.newButtonColumn('delete', '', 'id', 'float-right text-danger', 'deleteButton').withMatIcon('delete').withVisiblePath('manualActivation').withStyle({ '_any': { 'font-size': '20px', 'padding': '0px', 'color': '#DD4B39', 'width': '48px' } }));
                }
            } else {
                const deleteColumnIndex = this.displayedColumns.findIndex(c => c.name == 'delete');
                if (deleteColumnIndex > -1) {
                    this.displayedColumns.splice(deleteColumnIndex, 1);
                }
            }
            this.updateElementList(pagedList);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    updateElementList(pagedList: PagedList<WorkSession>): void {
        if (this.isMobile) {
            this.mobileListWidget.updateElementList(pagedList);
        } else {
            this.fillerRowCount = pagedList.numberOfElements;
            this.elements = pagedList.content;
            this.dataSource = new MatTableDataSource<WorkSession>(this.elements);
            this.length = (pagedList.number * pagedList.size) + pagedList.numberOfElements + (pagedList.last ? 0 : 1);
            this.pageSize = pagedList.size;
            this.pageIndex = pagedList.number;
            this.totalPages = pagedList.totalPages;
            this.loaded = true;
        }
    }

    refreshList(data?: { pageIndex: number, pageSize: number, advancedSearchBody: any, sort: string[] }): void {
        this.loaded = false;
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.getWorkSessionList();
    }

    protected goToWorkSessionDetails(id: string): void {
        if (this.detailsMode == DetailsModeType.PAGE) {
            if (this.detailsPath) {
                this.navigationService.navigateTo(['/dashboard/historical_work_session_details', id, this.detailsPath]);
            } else {
                this.navigationService.navigateTo(['/dashboard/historical_work_session_details', id]);
            }
        } else if (this.detailsMode == DetailsModeType.POPUP) {
            this.openDialog(id, true);
        }
    }

    protected subscribeToExportServices(): void {
        this.exportId = "historical-work-session-list-" + this.getNextId();
        this.exportService.subscribeToExport(this.exportId, this.title || "Historical Work Sessions").subscribe(() => this.export());
        this.subscribeToExportVisibility();
    }

    import() {
        this.refreshElementList();
    }

    protected deleteWorkSession(id: string): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            title: "deleteWorkSessionTitle",
            message: "deleteWorkSessionMessage"
        }
        firstValueFrom(this.dialog.open(ConfirmDialog, dialogConfig).afterClosed()).then(result => {
            if (result) {
                this.workSessionService.deleteHistoricalWorkSession(id).then(() => this.refreshElementList());
            }
        });
    }

    protected updateSnackbarButtons(): void {
        if (this.selectedElements.length) {
            const allElementsCount = this.allElementsSelected ? this.length : this.selectedElements.length;
            let dividerPosition = 0;
            this.snackbarButtons = [
                { type: ListSnackbarButtonType.SELECTED_ELEMENTS, label: allElementsCount + '', event: 'unselect' }
            ];
            if (!this.allElementsSelected) {
                this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'selectAllButton', event: 'selectAll', icon: 'select_all' });
                dividerPosition++;
            }
            if (!!this.thingContextService.getCurrentThing()) {
                this.snackbarButtons.push({ type: ListSnackbarButtonType.SIMPLE, label: 'generateReportsProperty', event: 'exportReports', icon: 'docs', disabled: this.selectedElements?.length > 100 || !this.selectedElements.some(ws => ws.reportStorageKey) });
            }
            this.dividerIndexes = [dividerPosition];
        } else {
            this.snackbarButtons = null;
            this.dividerIndexes = null;
        }
    }

    doSnackbarAction(event: string): void {
        switch (event) {
            case 'unselect':
                this.clearSelectedElements();
                break;
            case 'selectAll':
                this.setAllElementsSelected();
                break;
            case 'exportReports':
                this.exportReports();
                break;
        }
    }

    private exportReports(): void {
        let params = this.historicalWorkSessionListWidgetV2Service.getParams(this.searchFields, this.advancedSearchBody, false, this.includeSubThingsWorkSessions);
        let body = {};
        if (this.allElementsSelected) {
            params = params.set('selectAll', true);
        } else {
            body['elementIds'] = this.selectedElements.map(ws => ws.id);
        }
        const dialogRef = this.openDownloadDialog();
        this.historicalWorkSessionListWidgetV2Service.exportReports(body, params).then(() => {
            dialogRef.close();
        }).catch(err => {
            this.showSnackbar(ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR), 'notification-error');
            dialogRef.close();
        });
    }

    protected override exportReport(workSessionId: string): void {
        let params = this.historicalWorkSessionListWidgetV2Service.getParams(this.searchFields, this.advancedSearchBody, false, this.includeSubThingsWorkSessions);
        let body = {
            elementIds: [workSessionId]
        };
        const dialogRef = this.openDownloadDialog();
        this.historicalWorkSessionListWidgetV2Service.exportReports(body, params).then(() => {
            dialogRef.close();
        }).catch(err => {
            this.showSnackbar(ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR), 'notification-error');
            dialogRef.close();
        });
    }

    private openDownloadDialog(): any {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '10%';
        dialogConfig.disableClose = true;
        dialogConfig.panelClass = "download-progress-bar-dialog";
        dialogConfig.data = { title: "downloadsTitle" };
        return this.dialog.open(DownloadProgressBarDialogComponent, dialogConfig);
    }

    protected downloadData(id: string): void {
        const dialogRef = this.openDownloadDialog();
        this.historicalWorkSessionListWidgetV2Service.downloadData(id).then(() => {
            dialogRef.close();
        }).catch(err => {
            this.showSnackbar(ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR), 'notification-error');
            dialogRef.close();
        });
    }

    handleImport(): void {
        if (this.manualActivationWorkSessionDefinitions?.length) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.minWidth = '25%';
            dialogConfig.maxWidth = '528px';
            dialogConfig.autoFocus = false;
            dialogConfig.data = {
                workSessions: this.manualActivationWorkSessionDefinitions,
                thing: this.thingContextService.getCurrentThing(),
                reconcileConnectionStatus: this.reconcileConnectionStatus
            }
            firstValueFrom(this.dialog.open(WorkSessionAdvancedSearchImportDialogComponent, dialogConfig).afterClosed()).then(result => {
                if (result) {
                    this.import();
                }
            })
        };
    }

}